import type { Dispatch, FormEvent, RefObject, SetStateAction } from "react";
import type { ActiveRunSummary, AskUserQuestionAnswer, Message, ReferenceItem, RunDetail, SelectedSkill, SkillDefinition, ThinkingMode } from "../../api/types.js";
import { Composer } from "../composer/Composer.js";
import { ActiveRunNotice } from "../composer/ActiveRunNotice.js";
import { AskQuestionDock } from "./AskQuestionDock.js";
import { ChatStage } from "./ChatStage.js";
import { StarterPrompts } from "./StarterPrompts.js";

export type ChatPageProps = {
  active?: boolean;
  threadRef: RefObject<HTMLDivElement | null>;
  askDockRef: RefObject<HTMLDivElement | null>;
  isWelcome: boolean;
  isSessionLoading: boolean;
  messages: Message[];
  citationSources: ReferenceItem[];
  referencePoolTotalCount: number;
  expandedActivityMessageId: string | null;
  setExpandedActivityMessageId: (messageId: string | null) => void;
  onOpenCitation: (messageId: string, localIndex: number) => void;
  onOpenMessageSources: (messageId: string) => void;
  onOpenReferencePool: (messageId: string) => void;
  prompt: string;
  setPrompt: Dispatch<SetStateAction<string>>;
  submitting: boolean;
  running: boolean;
  plusOpen: boolean;
  setPlusOpen: Dispatch<SetStateAction<boolean>>;
  onSubmit: (event?: FormEvent) => void;
  onStop: () => void;
  onSaveLinkAsset: () => void;
  onUploadAsset: (file: File) => Promise<void>;
  onAttachRecent: (title: string) => void;
  skills: SkillDefinition[];
  onManageSkills: () => void;
  selectedSkill: SelectedSkill | null;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkill | null>>;
  thinkingMode: ThinkingMode;
  setThinkingMode: Dispatch<SetStateAction<ThinkingMode>>;
  pendingAskMessage: Message | null;
  onSubmitAskUserQuestion: (message: Message, answers: AskUserQuestionAnswer[]) => void;
  onCancelAskUserQuestion: (message: Message) => void;
  saveSourceAsAsset: (source: ReferenceItem) => void;
  activeRun: ActiveRunSummary | null;
  recoveringRun: RunDetail | null;
  currentSessionOwnsRun: boolean;
  hasStreamBufferForCurrentSession: boolean;
  stopPending: boolean;
  onReturnToActiveRun: () => void;
};

export function ChatPage({
  active = true,
  threadRef,
  askDockRef,
  isWelcome,
  isSessionLoading,
  messages,
  citationSources,
  referencePoolTotalCount,
  expandedActivityMessageId,
  setExpandedActivityMessageId,
  onOpenCitation,
  onOpenMessageSources,
  onOpenReferencePool,
  prompt,
  setPrompt,
  submitting,
  running,
  plusOpen,
  setPlusOpen,
  onSubmit,
  onStop,
  onSaveLinkAsset,
  onUploadAsset,
  onAttachRecent,
  skills,
  onManageSkills,
  selectedSkill,
  setSelectedSkill,
  thinkingMode,
  setThinkingMode,
  pendingAskMessage,
  onSubmitAskUserQuestion,
  onCancelAskUserQuestion,
  saveSourceAsAsset,
  activeRun,
  recoveringRun,
  currentSessionOwnsRun,
  hasStreamBufferForCurrentSession,
  stopPending,
  onReturnToActiveRun
}: ChatPageProps) {
  const failedRecovery = recoveringRun && recoveringRun.status === "failed" && !recoveringRun.assistantMessageId
    ? recoveringRun
    : null;
  // A recovered snapshot renders the durable partial answer and keeps this
  // status/stop affordance visible until the formal message hydrates.
  const placeholderRun = currentSessionOwnsRun && !hasStreamBufferForCurrentSession
    ? (activeRun || failedRecovery)
    : null;
  const quietlyFinalizing = Boolean(
    activeRun
    && currentSessionOwnsRun
    && hasStreamBufferForCurrentSession
    && activeRun.phase === "finalizing"
  );
  const composerMode = !activeRun
    ? "normal"
    : !currentSessionOwnsRun
      ? "return-notice"
      : hasStreamBufferForCurrentSession && ["running", "finalizing"].includes(activeRun.phase)
        ? "normal"
        : "phase-notice";
  return (
    <section className={`view chat-view ${active ? "workspace-view-visible" : "workspace-view-hidden"}`} aria-label="科研问答" aria-hidden={!active}>
      <ChatStage
        threadRef={threadRef}
        isWelcome={isWelcome}
        isSessionLoading={isSessionLoading}
        messages={messages}
        citationSources={citationSources}
        referencePoolTotalCount={referencePoolTotalCount}
        expandedActivityMessageId={expandedActivityMessageId}
        setExpandedActivityMessageId={setExpandedActivityMessageId}
        onOpenCitation={onOpenCitation}
        onOpenMessageSources={onOpenMessageSources}
        onOpenReferencePool={onOpenReferencePool}
        setPrompt={setPrompt}
        onSubmitAskUserQuestion={onSubmitAskUserQuestion}
        onCancelAskUserQuestion={onCancelAskUserQuestion}
        saveSourceAsAsset={saveSourceAsAsset}
        placeholderRun={placeholderRun}
        onStopActiveRun={onStop}
        stopPending={stopPending}
      />

      <AskQuestionDock
        askDockRef={askDockRef}
        pendingAskMessage={pendingAskMessage}
        running={submitting || running}
        onSubmit={onSubmitAskUserQuestion}
        onCancel={onCancelAskUserQuestion}
      />

      {composerMode === "normal" ? (
        <Composer
          prompt={prompt}
          setPrompt={setPrompt}
          submitting={submitting}
          settling={quietlyFinalizing}
          running={running}
          plusOpen={plusOpen}
          setPlusOpen={setPlusOpen}
          onSubmit={onSubmit}
          onStop={onStop}
          onSaveLinkAsset={onSaveLinkAsset}
          onUploadAsset={onUploadAsset}
          onAttachRecent={onAttachRecent}
          skills={skills}
          onManageSkills={onManageSkills}
          selectedSkill={selectedSkill}
          setSelectedSkill={setSelectedSkill}
          thinkingMode={thinkingMode}
          setThinkingMode={setThinkingMode}
        />
      ) : (
        <div className="composer active-run-notice-slot">
          <ActiveRunNotice
            run={activeRun!}
            currentSessionOwnsRun={currentSessionOwnsRun}
            onReturn={onReturnToActiveRun}
          />
        </div>
      )}

      {isWelcome && !activeRun && !submitting && <StarterPrompts setPrompt={setPrompt} />}
    </section>
  );
}
